// =============================================================================
// bookingAttachments.ts — files hung off a project booking.
// =============================================================================
// James, 21 Aug 2026, on the outage form:
//
//   "Maybe we have an attachment option to attach the CRQ approval email from
//    the carrier / client — sometimes it's a snip."
//
// "Sometimes it's a snip" is the whole requirement. The approval arrives as
// whatever the carrier felt like sending: a forwarded email, a PDF, or a
// screenshot pasted into Teams. So this accepts images and documents alike and
// does not try to parse any of it — the value is that the evidence is ON the
// booking when somebody asks "who said we could take this outage?", not that
// the app understood it.
//
// PATTERN
// -------
// Deliberately the SAME three steps as hopUpload / deliverablesUpload /
// clientPoUpload rather than a fourth way of attaching a file:
//
//   1. POST /sign-upload      → presigned PUT URL from the api-gateway Worker
//   2. PUT bytes straight to R2 (zero bytes through the Worker)
//   3. insert a public.files row (parent_table / parent_id polymorphic)
//
// Step 3 is a direct client insert rather than a new Worker endpoint, because
// files_insert is `with check (true)` for authenticated and there is nothing
// here that needs elevated rights. That also means NO Worker deploy is required
// to ship this.
//
// WHO CAN SEE IT
// --------------
// security_class 'internal' → app.is_staff(). A carrier's approval for a window
// our own techs have to work inside is not commercial-in-confidence, and a tech
// standing at the gate at 2am being asked for the authority is exactly the
// person who needs to open it.
//
// WHO CAN UPLOAD IT
// -----------------
// The Worker gates /sign-upload by key prefix: director and manager can write
// anywhere in coretel-prod, field and supervisor only under projects/…/forms,
// projects/…/acceptance-packs and inspections/…. `bookings/…` is therefore
// manager+, which matches project_bookings being manager+ to write anyway.
// =============================================================================

import { supabase, apiFetch } from "@/lib/supabase";

const BUCKET = "coretel-prod";

export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

// Generous on purpose. A carrier approval is a .msg, a .eml, a PDF or a phone
// screenshot, and refusing one of those means it ends up in somebody's inbox
// instead of on the booking, which is the failure this is meant to prevent.
export const ATTACHMENT_ACCEPT =
  "image/*,application/pdf,.msg,.eml,.doc,.docx,.txt";

export interface BookingFile {
  file_id:         string;
  file_name:       string;
  content_type:    string | null;
  file_size_bytes: number | null;
  r2_object_key:   string;
  uploaded_at:     string | null;
}

function objectKeyFor(bookingId: string, file: File): string {
  const safe = file.name.replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 80);
  return `bookings/${bookingId}/${Date.now()}-${safe}`;
}

/**
 * Upload one file and attach it to a booking.
 * Throws with a readable message on any step — the caller surfaces it.
 */
export async function uploadBookingAttachment(
  bookingId: string,
  file: File,
): Promise<void> {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    throw new Error(
      `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is ${MAX_ATTACHMENT_BYTES / 1024 / 1024} MB.`,
    );
  }

  const objectKey   = objectKeyFor(bookingId, file);
  const contentType = file.type || "application/octet-stream";

  // 1 — presigned PUT
  const signResp = await apiFetch("/sign-upload", {
    method: "POST",
    body: JSON.stringify({ bucket: BUCKET, object_key: objectKey, content_type: contentType }),
  });
  if (!signResp.ok) {
    const e = await signResp.json().catch(() => ({}));
    throw new Error(`Could not start the upload: ${(e as any)?.error ?? signResp.status}`);
  }
  const { upload_url } = await signResp.json() as { upload_url: string };

  // 2 — bytes straight to R2
  const put = await fetch(upload_url, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed: ${put.status} ${put.statusText}`);

  // 3 — record it. If this fails the object is orphaned in R2 rather than shown
  // as attached, which is the right way round: a file nobody can see is a
  // storage cost, a row pointing at nothing is a broken link in the UI.
  const { error } = await supabase.from("files").insert({
    parent_table:    "project_bookings",
    parent_id:       bookingId,
    r2_bucket:       BUCKET,
    r2_object_key:   objectKey,
    file_name:       file.name,
    content_type:    contentType,
    file_size_bytes: file.size,
    security_class:  "internal",
  });
  if (error) throw new Error(`Uploaded, but could not attach it: ${error.message}`);
}

/** Everything attached to a booking, newest first. */
export async function listBookingAttachments(bookingId: string): Promise<BookingFile[]> {
  const { data, error } = await supabase
    .from("files")
    .select("file_id, file_name, content_type, file_size_bytes, r2_object_key, uploaded_at")
    .eq("parent_table", "project_bookings")
    .eq("parent_id", bookingId)
    .order("uploaded_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as BookingFile[];
}

/** A short-lived signed URL to open one attachment. */
export async function bookingAttachmentUrl(objectKey: string): Promise<string> {
  const r = await apiFetch("/sign-download", {
    method: "POST",
    body: JSON.stringify({ bucket: BUCKET, object_key: objectKey }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(`Could not open that file: ${(e as any)?.error ?? r.status}`);
  }
  const { download_url } = await r.json() as { download_url: string };
  return download_url;
}

/**
 * Detach a file. Removes the files row only — the R2 object is left in place.
 * Deliberate: this is evidence of a carrier approval, and an accidental click
 * should not be able to destroy the only copy of it. Storage is cheap; a lost
 * approval during an incident review is not.
 */
export async function removeBookingAttachment(fileId: string): Promise<void> {
  const { error } = await supabase.from("files").delete().eq("file_id", fileId);
  if (error) throw error;
}
